'use client'

import { type Hit } from 'instantsearch.js'
import {
  type ChangeEvent,
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import {
  type AlgoliaQueryRecord,
  type AlgoliaRecord,
  type AlgoliaUserDataRedirect,
  useAlgoliaConfigContext,
} from '../../../algolia'
import { useGlobalConfigContext } from '../../../global-config'

import { type HeaderSearchContextValue } from './components/types'
import { headerSearchContextHandleSubmitSearch } from './headerSearchContextHandleSubmitSearch'
import { headerSearchSessionStorageTrackingTrackSuggestItemClick } from './headerSearchSessionStorageTracking'
import { usePreviousSearchControls } from './previousSearchesStorage'
import { useSearchClientControls } from './useSearchClientControls'
import { useSearchQuery } from './useSearchQuery'

const HeaderSearchContext = createContext<HeaderSearchContextValue | null>(null)
const HEADER_SEARCH_TYPEAHEAD_MIN_QUERY_LEN = 3
const ALGOLIA_SUGGEST_OPTIMISATION_DEBOUNCE_REQUEST_TIME = 500

/**
 * State management for search input. Important to note that searchQuery and actual value on input are not same state.
 * searchQuery will actually be used to query algolia, while actual value on input is used to display the value on input.
 * so at given time they may contain different values.
 * That was needed for autocompletion on arrow keys, due to input may contain some value that should not trigger actual search
 */
const useInputControls = (): HeaderSearchContextValue['inputControls'] => {
  const [searchQuery, setSearchQuery] = useSearchQuery(
    HEADER_SEARCH_TYPEAHEAD_MIN_QUERY_LEN,
  )
  const [searchInputValueOnState, setSearchInputValueOnState] =
    useSearchQuery(1)
  const [renderedSearchRecords, setRenderedSearchRecords] = useState<
    Hit<AlgoliaRecord>[]
  >([])
  const [algoliaUserDataRedirects, setAlgoliaUserDataRedirects] = useState<
    AlgoliaUserDataRedirect[]
  >([])
  const [renderedSuggestionRecords, setRenderedSuggestionRecords] = useState<
    AlgoliaQueryRecord[]
  >([])
  const debouncedSearchQueryUpdate = useDebounceCallback(
    setSearchQuery,
    ALGOLIA_SUGGEST_OPTIMISATION_DEBOUNCE_REQUEST_TIME,
  )

  /**
   * sets value on input and updates search query if needed
   * @param value value to be set on input
   * @param updateQuery if true search query will be updated to match value, effectively triggering search
   */
  const setSearchInputValue: HeaderSearchContextValue['inputControls']['setSearchInputValue'] =
    useCallback(
      (value, { updateSearchQuery }) => {
        if (
          updateSearchQuery &&
          value.length >= HEADER_SEARCH_TYPEAHEAD_MIN_QUERY_LEN
        ) {
          debouncedSearchQueryUpdate(value)
          if (value.length === HEADER_SEARCH_TYPEAHEAD_MIN_QUERY_LEN) {
            debouncedSearchQueryUpdate.flush()
          }
        }
        setSearchInputValueOnState(value)
      },
      [debouncedSearchQueryUpdate, setSearchInputValueOnState],
    )

  return useMemo((): HeaderSearchContextValue['inputControls'] => {
    return {
      algoliaUserDataRedirects,
      clearSearch: (): void => {
        setSearchInputValue('', { updateSearchQuery: true })
        setSearchQuery('')
      },
      handleInputChange: (event: ChangeEvent<HTMLInputElement>): void => {
        setSearchInputValue(event.target.value, {
          updateSearchQuery: true,
        })
        if (event.target.value.length < HEADER_SEARCH_TYPEAHEAD_MIN_QUERY_LEN) {
          setSearchQuery('')
        }
      },
      renderedSearchRecords,
      renderedSuggestionRecords,

      /**
       * value on input itself, when needed it can be used to update search query
       */
      searchInputValue: searchInputValueOnState,
      searchQuery,
      setAlgoliaUserDataRedirects,
      setRenderedSearchRecords,
      setRenderedSuggestionRecords,
      setSearchInputValue,
    }
  }, [
    renderedSearchRecords,
    algoliaUserDataRedirects,
    renderedSuggestionRecords,
    searchInputValueOnState,
    searchQuery,
    setSearchQuery,
    setSearchInputValue,
  ])
}
/**
 * State management for header search.
 * @remark: part of header search/results is loaded dynamically. Dynamically loaded component will set
 * e.g. client search to context, so it's as well available higher than dynamic loaded components.
 */
export const HeaderSearchContextProvider: FC<{
  children: ReactNode
}> = ({ children }) => {
  const [searchIsActive, setSearchIsActive] = useState(false)
  const searchClientControls = useSearchClientControls()
  const previousSearchControls = usePreviousSearchControls()
  const inputControls = useInputControls()
  const configuration = useAlgoliaConfigContext()
  const globalConfig = useGlobalConfigContext()
  const [portalResultsTo, setPortalResultsTo] = useState<
    HTMLDivElement | null | undefined
  >(undefined)

  const contextValue: HeaderSearchContextValue = useMemo(
    (): HeaderSearchContextValue => ({
      configuration,
      handleSubmitSearch: (): void => {
        headerSearchContextHandleSubmitSearch(globalConfig, inputControls)
      },
      inputControls,
      portalResultsTo,
      previousSearchControls,
      searchClientControls,
      searchIsActive,
      setSearchIsActive: ({
        portalResultsToElement,
        value,
      }: {
        portalResultsToElement?: HTMLDivElement | null
        value: boolean
      }): void => {
        setSearchIsActive(value)
        setPortalResultsTo(portalResultsToElement)
      },
      /**
       * if search is active but user did not yet type anything, some default recommendations: top queries and top seller products will be shown
       */
      shouldShowDefaultRecommendations: (): boolean => {
        return searchIsActive && inputControls.searchQuery.length === 0
      },

      /**
       * https://jira.shop-apotheke.com/browse/WSAWA-2281
       */
      trackSuggestItemClick: (): void => {
        headerSearchSessionStorageTrackingTrackSuggestItemClick({
          qSuggest: inputControls.renderedSearchRecords?.length ?? 0,
          searchPhrase: inputControls.searchQuery,
        })
      },
    }),
    [
      configuration,
      globalConfig,
      inputControls,
      portalResultsTo,
      searchClientControls,
      searchIsActive,
      previousSearchControls,
    ],
  )

  return (
    <HeaderSearchContext.Provider value={contextValue}>
      {children}
    </HeaderSearchContext.Provider>
  )
}

export const useHeaderSearchContext = (): HeaderSearchContextValue => {
  const contextValue = useContext(HeaderSearchContext)
  if (!contextValue) {
    throw new Error(
      'useHeaderSearchStore must be used within a HeaderSearchStoreProvider',
    )
  }

  return contextValue
}
